import { ArrowLeft, Mail, ShieldX } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui-kit/button';
import { Card, CardContent } from '@/components/ui-kit/card';
import { Separator } from '@/components/ui-kit/separator';

/**
 * Where "Contact Support" points. Unset in most environments, in which case
 * the button is simply not offered — better than sending people to an address
 * that doesn't exist.
 */
const SUPPORT_EMAIL = (import.meta.env.VITE_SUPPORT_EMAIL as string | undefined) ?? '';

interface ForbiddenCardProps {
  /** Overrides the generic org-membership message with something resource-specific. */
  description?: string;
  /** Rendered as a "Go Back" button when provided. */
  onBack?: () => void;
  /** Overrides the env-configured support address. */
  supportEmail?: string;
}

/**
 * Terminal 403 state, rendered in place of a page.
 *
 * Deliberately does NOT redirect: the URL stays put so the person can see what
 * they tried to open, and so a shared link doesn't silently become a different
 * page for whoever lacks access.
 */
export function ForbiddenCard({ description, onBack, supportEmail }: ForbiddenCardProps) {
  const { t } = useTranslation();
  const mailTo = supportEmail ?? SUPPORT_EMAIL;

  return (
    <div className="container max-w-2xl mx-auto px-4 py-16">
      <Card className="overflow-hidden">
        <CardContent className="flex flex-col items-center px-6 py-10 text-center sm:px-10 sm:py-12">
          <div className="flex size-20 items-center justify-center rounded-full bg-crimson/10">
            <ShieldX className="size-9 text-crimson" />
          </div>

          <p className="mt-6 font-heading text-7xl font-bold leading-none tracking-tight text-crimson sm:text-8xl">
            403
          </p>
          <span className="mt-4 h-1 w-16 rounded-full bg-crimson" aria-hidden="true" />

          <h1 className="mt-5 font-heading text-2xl font-bold text-[hsl(var(--fg))]">
            {t('FORBIDDEN_TITLE', { defaultValue: 'Access Forbidden' })}
          </h1>

          <p className="mt-3 max-w-md text-globe-grey leading-relaxed">
            {description ??
              t('FORBIDDEN_DESCRIPTION', {
                defaultValue:
                  "You don't have access to this. It belongs to an organization you're not a member of.",
              })}
          </p>

          {(onBack || mailTo) && (
            <div className="mt-8 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:justify-center">
              {onBack && (
                <Button
                  onClick={onBack}
                  className="bg-crimson text-white hover:bg-crimson/90 focus-visible:bg-crimson"
                >
                  <ArrowLeft className="size-4" />
                  {t('FORBIDDEN_GOBACK', { defaultValue: 'Go Back' })}
                </Button>
              )}
              {mailTo && (
                <Button
                  asChild
                  variant="outline"
                  className="border-crimson/40 text-crimson hover:bg-crimson/5 hover:text-crimson"
                >
                  <a href={`mailto:${mailTo}`}>
                    <Mail className="size-4" />
                    {t('FORBIDDEN_CONTACTSUPPORT', { defaultValue: 'Contact Support' })}
                  </a>
                </Button>
              )}
            </div>
          )}

          <Separator className="my-8" />

          <p className="max-w-md text-sm text-muted-foreground leading-relaxed">
            {t('FORBIDDEN_FOOTNOTE', {
              defaultValue:
                'If you believe this is an error, please contact our support team with the details of what you were trying to access.',
            })}
          </p>

          <p className="mt-4 text-xs text-muted-foreground/70">
            {t('FORBIDDEN_HTTPLABEL', { defaultValue: 'HTTP 403 • Forbidden' })}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export default ForbiddenCard;
